import { PostData } from "./FetchData";

const MS1 = "ms1";
const BASE = "api/v1/website";

function parseMs1Response(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "failure") {
		const message =
			typeof res.message === "string" ? res.message : fallbackMessage;
		return { ok: false, message, data: res.data ?? null };
	}
	if (res?.status !== "success") {
		const message =
			typeof res?.message === "string"
				? res.message
				: res?.message?.message || fallbackMessage;
		return { ok: false, message, data: null };
	}
	return { ok: true, message: res.message ?? null, data: res.data ?? null };
}

function requireWebsiteId(websiteId) {
	if (websiteId == null || websiteId === "") {
		return { ok: false, message: "Missing website id", data: null };
	}
	return null;
}

async function postMonitoring(path, body, fallbackMessage) {
	const res = await PostData(MS1, `${BASE}/${path}`, body);
	return parseMs1Response(res, fallbackMessage);
}

export async function listMonitoringProjects(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, projects: [], total: 0 };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringProjects",
		{ websiteId: Number(websiteId) },
		"Failed to load monitoring projects"
	);
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, projects: [], total: 0 };
	}
	const projects = Array.isArray(parsed.data?.projects) ? parsed.data.projects : [];
	const total =
		parsed.data?.total != null ? Number(parsed.data.total) : projects.length;
	return { ok: true, message: parsed.message, projects, total };
}

export async function getMonitoringProject(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, project: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringProject",
		{ websiteId: Number(websiteId) },
		"Failed to load monitoring project"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, project: null };
	return { ok: true, message: parsed.message, project: parsed.data };
}

export async function enableMonitoring(websiteId, { brandName, baselineAuditId } = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, result: null };
	const body = { websiteId: Number(websiteId) };
	if (brandName) body.brandName = brandName;
	if (baselineAuditId) body.baselineAuditId = baselineAuditId;
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringEnable",
		body,
		"Failed to enable monitoring"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, result: null };
	return { ok: true, message: parsed.message, result: parsed.data };
}

export async function pauseMonitoring(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringPause",
		{ websiteId: Number(websiteId) },
		"Failed to pause monitoring"
	);
}

export async function resumeMonitoring(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringResume",
		{ websiteId: Number(websiteId) },
		"Failed to resume monitoring"
	);
}

export async function runMonitoringNow(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringRunNow",
		{ websiteId: Number(websiteId) },
		"Failed to queue prompt run"
	);
}

export async function reauditMonitoringNow(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringReauditNow",
		{ websiteId: Number(websiteId) },
		"Failed to start re-audit"
	);
}

export async function getMonitoringCrawlPages(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, crawl: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringCrawlPages",
		{ websiteId: Number(websiteId) },
		"Failed to load crawl pages"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, crawl: null };
	const data = parsed.data || {};
	const urls = Array.isArray(data.url)
		? data.url
		: Array.isArray(data.urls)
			? data.urls
			: [];
	const selected = Array.isArray(data.selected) ? data.selected : [];
	const modeRaw = String(data.mode || "auto").toLowerCase();
	const mode =
		modeRaw === "manual" || modeRaw === "all" ? modeRaw : "auto";
	return {
		ok: true,
		message: parsed.message,
		crawl: {
			urls: urls.map((u) => String(u || "").trim()).filter(Boolean),
			total: data.total != null ? Number(data.total) : urls.length,
			sitemapUrl: data.sitemap_url || null,
			source: data.source || null,
			blocked: Boolean(data.blocked),
			sitemapExists:
				data.sitemap_exists !== false && Boolean(data.sitemap_url),
			selected: selected.map((u) => String(u || "").trim()).filter(Boolean),
			mode,
			maxPages: data.max_pages != null ? Number(data.max_pages) : 30,
			ceiling: data.ceiling != null ? Number(data.ceiling) : 200,
		},
	};
}

export async function updateMonitoringSettings(
	websiteId,
	settingsOverrides,
	{ brandName } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, project: null };
	const body = {
		websiteId: Number(websiteId),
		settingsOverrides: settingsOverrides || {},
	};
	if (brandName != null && String(brandName).trim()) {
		body.brandName = String(brandName).trim();
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringUpdate",
		body,
		"Failed to save settings"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, project: null };
	return { ok: true, message: parsed.message, project: parsed.data ?? null };
}

function normalizeAliasNames(names) {
	const list = Array.isArray(names) ? names : [names];
	return list.map((n) => String(n || "").trim()).filter(Boolean);
}

export async function approveMonitoringAlias(websiteId, names) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, project: null };
	const aliasNames = normalizeAliasNames(names);
	if (!aliasNames.length) {
		return { ok: false, message: "Alias name is required", project: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringApproveBrandAliases",
		{ websiteId: Number(websiteId), names: aliasNames },
		"Failed to approve alias"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, project: null };
	return { ok: true, message: parsed.message, project: parsed.data ?? null };
}

export async function dismissMonitoringAlias(websiteId, names) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, project: null };
	const aliasNames = normalizeAliasNames(names);
	if (!aliasNames.length) {
		return { ok: false, message: "Alias name is required", project: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringDismissBrandAliases",
		{ websiteId: Number(websiteId), names: aliasNames },
		"Failed to dismiss alias"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, project: null };
	return { ok: true, message: parsed.message, project: parsed.data ?? null };
}

export async function getMonitoringOverview(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, overview: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringOverview",
		{ websiteId: Number(websiteId) },
		"Failed to load overview"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, overview: null };
	return { ok: true, message: parsed.message, overview: parsed.data };
}

export async function getMonitoringHistory(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, history: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringHistory",
		{ websiteId: Number(websiteId) },
		"Failed to load history"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, history: null };
	return { ok: true, message: parsed.message, history: parsed.data };
}

export async function getMonitoringPrompts(websiteId, { engine, status } = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) {
		return {
			ok: false,
			message: missing.message,
			prompts: [],
			total: 0,
			webSearch: false,
			engineFilter: null,
		};
	}
	const body = { websiteId: Number(websiteId) };
	if (engine) body.engine = engine;
	if (status === "all" || status === "retired") {
		body.status = status;
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringPrompts",
		body,
		"Failed to load prompts"
	);
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			prompts: [],
			total: 0,
			webSearch: false,
			engineFilter: null,
		};
	}
	return {
		ok: true,
		message: parsed.message,
		prompts: Array.isArray(parsed.data?.prompts) ? parsed.data.prompts : [],
		total: Number(parsed.data?.total ?? 0),
		maxPersonasPerPrompt: Number(parsed.data?.max_personas_per_prompt ?? 0),
		webSearch: Boolean(parsed.data?.web_search),
		engineFilter: parsed.data?.engine_filter ?? null,
	};
}

export async function getMonitoringPromptDetail(websiteId, promptId, days = 30) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, detail: null };
	if (promptId == null) {
		return { ok: false, message: "Missing prompt id", detail: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringPromptDetail",
		{
			websiteId: Number(websiteId),
			promptId: Number(promptId),
			days: Number(days) || 30,
		},
		"Failed to load prompt detail"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, detail: null };
	return { ok: true, message: parsed.message, detail: parsed.data };
}

export async function getMonitoringTopics(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) {
		return { ok: false, message: missing.message, topics: [], unassigned: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringTopics",
		{ websiteId: Number(websiteId) },
		"Failed to load topics"
	);
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			topics: [],
			unassigned: null,
		};
	}
	return {
		ok: true,
		message: parsed.message,
		topics: Array.isArray(parsed.data?.topics) ? parsed.data.topics : [],
		unassigned: parsed.data?.unassigned ?? null,
	};
}

export async function addMonitoringPrompt(websiteId, payload) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const text =
		typeof payload === "string" ? payload : payload?.promptText || "";
	if (!text?.trim()) {
		return { ok: false, message: "Prompt text is required", data: null };
	}
	const body = {
		websiteId: Number(websiteId),
		promptText: text.trim(),
	};
	if (payload && typeof payload === "object") {
		const hasTopic =
			payload.topicId != null &&
			payload.topicId !== "" &&
			!Number.isNaN(Number(payload.topicId));
		if (payload.autoTopic === true || (!hasTopic && !payload.createTopicName)) {
			body.autoTopic = true;
		} else {
			body.autoTopic = false;
			if (hasTopic) body.topicId = Number(payload.topicId);
			if (payload.createTopicName) {
				body.createTopicName = String(payload.createTopicName).trim();
			}
		}
		if (payload.intent) body.intent = payload.intent;
		if (payload.journeyStage) body.journeyStage = payload.journeyStage;
		if (payload.isBranded != null) body.isBranded = Boolean(payload.isBranded);
		if (Array.isArray(payload.personaIds) && payload.personaIds.length) {
			body.personaIds = payload.personaIds.map((id) => Number(id));
		}
		if (payload.runWithoutPersona === false) {
			body.runWithoutPersona = false;
		}
	} else {
		body.autoTopic = true;
	}
	return postMonitoring(
		"aiVisibilityMonitoringAddPrompt",
		body,
		"Failed to add prompt"
	);
}

export async function setMonitoringPromptPersonas(websiteId, promptId, personaIds) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (promptId == null) {
		return { ok: false, message: "Missing prompt id", data: null };
	}
	return postMonitoring(
		"aiVisibilityMonitoringSetPromptPersonas",
		{
			websiteId: Number(websiteId),
			promptId: Number(promptId),
			personaIds: Array.isArray(personaIds)
				? personaIds.map((id) => Number(id))
				: [],
		},
		"Failed to save personas"
	);
}

export async function setMonitoringPromptNoPersona(websiteId, promptId, enabled) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (promptId == null) {
		return { ok: false, message: "Missing prompt id", data: null };
	}
	return postMonitoring(
		"aiVisibilityMonitoringSetPromptNoPersona",
		{
			websiteId: Number(websiteId),
			promptId: Number(promptId),
			enabled: Boolean(enabled),
		},
		"Failed to update no-persona run"
	);
}

export async function getMonitoringPromptLatestAnalysis(websiteId, promptId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, analysis: null };
	if (promptId == null) {
		return { ok: false, message: "Missing prompt id", analysis: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringPromptLatestAnalysis",
		{
			websiteId: Number(websiteId),
			promptId: Number(promptId),
		},
		"Could not load latest analysis"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, analysis: null };
	return { ok: true, message: parsed.message, analysis: parsed.data ?? null };
}

export async function createMonitoringTopic(websiteId, name, intentPrimary) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, topic: null };
	const trimmed = String(name || "").trim();
	if (!trimmed) {
		return { ok: false, message: "Topic name is required", topic: null };
	}
	const body = {
		websiteId: Number(websiteId),
		name: trimmed,
	};
	if (intentPrimary) body.intentPrimary = String(intentPrimary);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringCreateTopic",
		body,
		"Failed to create topic"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, topic: null };
	return { ok: true, message: parsed.message, topic: parsed.data };
}

export async function retireMonitoringPrompt(websiteId, promptId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringRetirePrompt",
		{ websiteId: Number(websiteId), promptId: Number(promptId) },
		"Failed to retire prompt"
	);
}

export async function reactivateMonitoringPrompt(websiteId, promptId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringReactivatePrompt",
		{ websiteId: Number(websiteId), promptId: Number(promptId) },
		"Failed to re-enable prompt"
	);
}

export async function updateMonitoringPrompt(websiteId, promptId, fields = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (promptId == null) {
		return { ok: false, message: "Missing prompt id", data: null };
	}
	const body = {
		websiteId: Number(websiteId),
		promptId: Number(promptId),
	};
	if (
		fields.topicId != null &&
		fields.topicId !== "" &&
		!Number.isNaN(Number(fields.topicId))
	) {
		body.topicId = Number(fields.topicId);
	}
	if (fields.intent != null && fields.intent !== "") {
		body.intent = String(fields.intent);
	}
	if (fields.journeyStage != null && fields.journeyStage !== "") {
		body.journeyStage = String(fields.journeyStage);
	}
	if (fields.isBranded != null) {
		body.isBranded = Boolean(fields.isBranded);
	}
	return postMonitoring(
		"aiVisibilityMonitoringUpdatePrompt",
		body,
		"Failed to update prompt"
	);
}

export async function renameMonitoringTopic(websiteId, topicId, name) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringUpdateTopic",
		{
			websiteId: Number(websiteId),
			topicId: Number(topicId),
			name: String(name || "").trim(),
		},
		"Failed to rename topic"
	);
}

export async function mergeMonitoringTopics(
	websiteId,
	sourceTopicIds,
	targetTopicId
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const sources = (Array.isArray(sourceTopicIds)
		? sourceTopicIds
		: [sourceTopicIds]
	)
		.map((id) => Number(id))
		.filter((n) => !Number.isNaN(n));
	return postMonitoring(
		"aiVisibilityMonitoringMergeTopics",
		{
			websiteId: Number(websiteId),
			sourceTopicIds: sources,
			targetTopicId: Number(targetTopicId),
		},
		"Failed to merge topics"
	);
}

export async function deleteMonitoringTopic(websiteId, topicId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringDeleteTopic",
		{ websiteId: Number(websiteId), topicId: Number(topicId) },
		"Failed to delete topic"
	);
}

export async function reassignMonitoringTopic(
	websiteId,
	topicId,
	{ promptIds = [], keywordIds = [] } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	if (topicId == null || Number.isNaN(Number(topicId))) {
		return { ok: false, message: "Topic is required", data: null };
	}
	return postMonitoring(
		"aiVisibilityMonitoringReassignTopic",
		{
			websiteId: Number(websiteId),
			topicId: Number(topicId),
			promptIds: (promptIds || [])
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n)),
			keywordIds: (keywordIds || [])
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n)),
		},
		"Failed to reassign topic"
	);
}

/** Prompts page — reassign selected prompts via promptIds. */
export async function moveMonitoringPrompts(websiteId, promptIds, topicId) {
	return reassignMonitoringTopic(websiteId, topicId, {
		promptIds,
		keywordIds: [],
	});
}

/** Keywords page — reassign selected keywords via keywordIds. */
export async function moveMonitoringKeywords(websiteId, keywordIds, topicId) {
	return reassignMonitoringTopic(websiteId, topicId, {
		promptIds: [],
		keywordIds,
	});
}

export async function splitMonitoringTopic(
	websiteId,
	{ topicId, name, promptIds = [], keywordIds = [] } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringSplitTopic",
		{
			websiteId: Number(websiteId),
			topicId: Number(topicId),
			name: String(name || "").trim(),
			promptIds: (promptIds || [])
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n)),
			keywordIds: (keywordIds || [])
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n)),
		},
		"Failed to split selection"
	);
}

export async function autoAssignMonitoringPrompts(
	websiteId,
	{ scope = "prompts" } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringAutoAssignTopics",
		{ websiteId: Number(websiteId), scope },
		"Failed to auto-assign topics"
	);
}

export async function getMonitoringCompetitors(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, competitors: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringCompetitors",
		{ websiteId: Number(websiteId) },
		"Failed to load competitors"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, competitors: null };
	return { ok: true, message: parsed.message, competitors: parsed.data };
}

export async function suggestMonitoringCompetitors(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, suggestions: [], provider: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringSuggestCompetitors",
		{ websiteId: Number(websiteId) },
		"Failed to suggest competitors"
	);
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, suggestions: [], provider: null };
	}
	return {
		ok: true,
		message: parsed.message,
		provider: parsed.data?.provider ?? null,
		suggestions: Array.isArray(parsed.data?.suggestions) ? parsed.data.suggestions : [],
	};
}

export async function addMonitoringCompetitor(websiteId, { name, domain } = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringAddCompetitor",
		{
			websiteId: Number(websiteId),
			name: String(name || "").trim(),
			domain: String(domain || "").trim(),
		},
		"Failed to add competitor"
	);
}

export async function updateMonitoringCompetitor(
	websiteId,
	{ competitorId, name, domain } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringUpdateCompetitor",
		{
			websiteId: Number(websiteId),
			competitorId: Number(competitorId),
			name: String(name || "").trim(),
			domain: String(domain || "").trim(),
		},
		"Failed to update competitor"
	);
}

export async function trackMonitoringCompetitor(websiteId, competitorId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringTrackCompetitor",
		{ websiteId: Number(websiteId), competitorId: Number(competitorId) },
		"Failed to track competitor"
	);
}

export async function ignoreMonitoringCompetitor(websiteId, competitorId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringIgnoreCompetitor",
		{ websiteId: Number(websiteId), competitorId: Number(competitorId) },
		"Failed to ignore competitor"
	);
}

export async function getMonitoringAlerts(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, alerts: [], rules: [] };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringAlerts",
		{ websiteId: Number(websiteId) },
		"Failed to load alerts"
	);
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, alerts: [], rules: [] };
	}
	return {
		ok: true,
		message: parsed.message,
		alerts: Array.isArray(parsed.data?.alerts) ? parsed.data.alerts : [],
		rules: Array.isArray(parsed.data?.rules) ? parsed.data.rules : [],
	};
}

export async function acknowledgeMonitoringAlert(websiteId, alertId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringAcknowledgeAlert",
		{ websiteId: Number(websiteId), alertId: Number(alertId) },
		"Failed to acknowledge alert"
	);
}

export async function getMonitoringKeywords(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) {
		return {
			...missing,
			keywords: [],
			groups: [],
			topics: [],
			total: 0,
			active: 0,
			cacheFresh: null,
		};
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringKeywords",
		{ websiteId: Number(websiteId) },
		"Failed to load keywords"
	);
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			keywords: [],
			groups: [],
			topics: [],
			total: 0,
			active: 0,
			cacheFresh: null,
		};
	}
	const data = parsed.data || {};
	return {
		ok: true,
		message: parsed.message,
		keywords: Array.isArray(data.keywords) ? data.keywords : [],
		groups: Array.isArray(data.groups) ? data.groups : [],
		topics: Array.isArray(data.topics) ? data.topics : [],
		total: data.total != null ? Number(data.total) : 0,
		active:
			data.active != null
				? Number(data.active)
				: data.active_count != null
					? Number(data.active_count)
					: 0,
		cacheFresh: data.cache_fresh ?? data.cacheFresh ?? null,
	};
}

export async function getKeywordPrompts(websiteId, keywordId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, keywordId: null, keyword: null, prompts: [], total: 0 };
	if (keywordId == null || keywordId === "") {
		return {
			ok: false,
			message: "Missing keyword id",
			keywordId: null,
			keyword: null,
			prompts: [],
			total: 0,
		};
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringKeywordPrompts",
		{
			websiteId: Number(websiteId),
			keywordId: Number(keywordId),
		},
		"Failed to load keyword prompts"
	);
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			keywordId: null,
			keyword: null,
			prompts: [],
			total: 0,
		};
	}
	const data = parsed.data || {};
	return {
		ok: true,
		message: parsed.message,
		keywordId: data.keyword_id ?? data.keywordId ?? Number(keywordId),
		keyword: data.keyword ?? null,
		prompts: Array.isArray(data.prompts) ? data.prompts : [],
		total: data.total != null ? Number(data.total) : (data.prompts || []).length,
	};
}

export async function getKeywordPromptCandidates(websiteId, status = "candidate") {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, total: 0, groups: [], status };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringKeywordPromptCandidates",
		{ websiteId: Number(websiteId), status: String(status || "candidate") },
		"Failed to load keyword prompt candidates"
	);
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, total: 0, groups: [], status };
	}
	const data = parsed.data || {};
	return {
		ok: true,
		message: parsed.message,
		total: data.total != null ? Number(data.total) : 0,
		groups: Array.isArray(data.groups) ? data.groups : [],
		status: data.status_filter || status,
	};
}

export async function generateKeywordPrompts(websiteId, { keywordIds = [] } = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringGenerateKeywordPrompts",
		{
			websiteId: Number(websiteId),
			keywordIds: (Array.isArray(keywordIds) ? keywordIds : [])
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n)),
		},
		"Failed to generate keyword prompts"
	);
}

export async function acceptKeywordPrompt(websiteId, promptId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringAcceptKeywordPrompt",
		{ websiteId: Number(websiteId), promptId: Number(promptId) },
		"Failed to track prompt"
	);
}

export async function rejectKeywordPrompt(websiteId, promptId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringRejectKeywordPrompt",
		{ websiteId: Number(websiteId), promptId: Number(promptId) },
		"Failed to reject prompt"
	);
}

export async function bulkKeywordPrompts(websiteId, action, promptIds = []) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringBulkKeywordPrompts",
		{
			websiteId: Number(websiteId),
			action: String(action || "stage"),
			promptIds: (promptIds || []).map((id) => Number(id)).filter((n) => !Number.isNaN(n)),
		},
		"Failed to update prompts"
	);
}

export async function discardAllKeywordCandidates(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringDiscardAllKeywordCandidates",
		{ websiteId: Number(websiteId) },
		"Failed to discard candidates"
	);
}

/** Deploy candidate prompts straight to active monitoring (Prompt Research tab). */
export async function deployKeywordPrompts(
	websiteId,
	{ promptIds = [], all = false } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, deployed: 0 };
	const body = { websiteId: Number(websiteId) };
	if (all) {
		body.all = true;
	} else {
		body.promptIds = (promptIds || [])
			.map((id) => Number(id))
			.filter((n) => !Number.isNaN(n));
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringDeployKeywordPrompts",
		body,
		"Failed to track prompt(s)"
	);
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, data: null, deployed: 0 };
	}
	const data = parsed.data || {};
	const deployed = Number(data.deployed ?? data.deployed_count ?? 0);
	return {
		ok: true,
		message: parsed.message,
		data,
		deployed: Number.isNaN(deployed) ? 0 : deployed,
	};
}

/** Audit-grounded context used when generating keyword prompts. */
export async function getKeywordGenerationContext(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, context: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringKeywordGenerationContext",
		{ websiteId: Number(websiteId) },
		"Failed to load generation context"
	);
	if (!parsed.ok) {
		return { ok: false, message: parsed.message, context: null };
	}
	return { ok: true, message: parsed.message, context: parsed.data };
}

export async function importMonitoringKeywords(
	websiteId,
	{ keywords, topicId = null, isActive = false, groupIntoTopics = true } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const list = Array.isArray(keywords)
		? keywords
				.map((k) => {
					if (k && typeof k === "object") {
						const keyword = String(k.keyword || k.text || "").trim();
						if (!keyword) return null;
						const row = { keyword };
						const intent = k.intent != null ? String(k.intent).trim() : "";
						const journey =
							k.journey_stage != null
								? String(k.journey_stage).trim()
								: k.journeyStage != null
									? String(k.journeyStage).trim()
									: "";
						if (intent) row.intent = intent;
						if (journey) row.journey_stage = journey;
						return row;
					}
					const s = String(k || "").trim();
					return s || null;
				})
				.filter(Boolean)
		: [];
	const body = {
		websiteId: Number(websiteId),
		keywords: list,
		isActive: Boolean(isActive),
		groupIntoTopics: Boolean(groupIntoTopics),
	};
	if (topicId != null && topicId !== "") body.topicId = Number(topicId);
	return postMonitoring(
		"aiVisibilityMonitoringImportKeywords",
		body,
		"Failed to import keywords"
	);
}

export async function activateMonitoringKeyword(websiteId, keywordId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringActivateKeyword",
		{ websiteId: Number(websiteId), keywordId: Number(keywordId) },
		"Failed to activate keyword"
	);
}

export async function deactivateMonitoringKeyword(websiteId, keywordId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringDeactivateKeyword",
		{ websiteId: Number(websiteId), keywordId: Number(keywordId) },
		"Failed to deactivate keyword"
	);
}

export async function setMonitoringKeywordsActive(
	websiteId,
	{ isActive, scope = "ids", topicId = null, keywordIds = null } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const body = {
		websiteId: Number(websiteId),
		isActive: Boolean(isActive),
		scope: String(scope || "ids"),
	};
	if (topicId != null && topicId !== "") body.topicId = Number(topicId);
	if (Array.isArray(keywordIds)) {
		body.keywordIds = keywordIds
			.map((id) => Number(id))
			.filter((n) => !Number.isNaN(n));
	}
	return postMonitoring(
		"aiVisibilityMonitoringSetKeywordsActive",
		body,
		"Failed to update keywords"
	);
}

export async function deleteMonitoringKeywords(websiteId, keywordIds = []) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringDeleteKeywords",
		{
			websiteId: Number(websiteId),
			keywordIds: (keywordIds || [])
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n)),
		},
		"Failed to delete keywords"
	);
}

export async function researchMonitoringKeywords(
	websiteId,
	{ locale = "en-US", limit = 100, seedKeywords } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const body = {
		websiteId: Number(websiteId),
		locale: String(locale || "en-US"),
		limit: Number(limit) || 100,
	};
	if (Array.isArray(seedKeywords) && seedKeywords.length) {
		body.seedKeywords = seedKeywords
			.map((k) => String(k || "").trim())
			.filter(Boolean);
	}
	return postMonitoring(
		"aiVisibilityMonitoringResearchKeywords",
		body,
		"Failed to research keywords"
	);
}

/** Bulk-refresh search volume, AI volume, and related metrics for tracked keywords. */
export async function refreshMonitoringKeywordVolume(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringRefreshKeywordVolume",
		{ websiteId: Number(websiteId) },
		"Failed to refresh keyword volume"
	);
}

/** Start fan-out + strategy analysis for a tracked prompt. */
export async function analyseMonitoringPrompt(websiteId, promptId) {
	if (websiteId == null || promptId == null) {
		return { ok: false, message: "Missing website or prompt id", analysis: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringAnalysePrompt",
		{
			websiteId: Number(websiteId),
			promptId: Number(promptId),
		},
		"Could not start analysis"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, analysis: null };
	return { ok: true, message: parsed.message, analysis: parsed.data };
}

/** Poll analysis status + result until complete or failed. */
export async function getMonitoringPromptAnalysis(websiteId, analysisId) {
	if (websiteId == null || analysisId == null) {
		return { ok: false, message: "Missing ids", analysis: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringPromptAnalysis",
		{ websiteId: Number(websiteId), analysisId: Number(analysisId) },
		"Could not load analysis"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, analysis: null };
	return { ok: true, message: parsed.message, analysis: parsed.data };
}

function trafficBody(websiteId, extra = {}) {
	return { websiteId: Number(websiteId), ...extra };
}

/** Main AI Traffic dashboard payload (TrafficData). */
export async function getMonitoringTraffic(
	websiteId,
	{
		start,
		end,
		compareStart,
		compareEnd,
		verifiedOnly = true,
		assistants,
		days,
	} = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, traffic: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringTraffic",
		trafficBody(websiteId, {
			...(start ? { start } : {}),
			...(end ? { end } : {}),
			...(compareStart ? { compareStart } : {}),
			...(compareEnd ? { compareEnd } : {}),
			...(verifiedOnly != null ? { verifiedOnly } : {}),
			...(assistants ? { assistants } : {}),
			...(days != null ? { days: Number(days) } : {}),
		}),
		"Failed to load AI traffic"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, traffic: null };
	return { ok: true, message: parsed.message, traffic: parsed.data };
}

export async function syncMonitoringTraffic(websiteId, daysBack = 14) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringTrafficSync",
		trafficBody(websiteId, { daysBack: Number(daysBack) }),
		"Failed to sync AI traffic"
	);
}

export async function getMonitoringTrafficBounds(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, bounds: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringTrafficBounds",
		trafficBody(websiteId),
		"Failed to load traffic date bounds"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, bounds: null };
	return { ok: true, message: parsed.message, bounds: parsed.data };
}

export async function getMonitoringTrafficCoverage(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, coverage: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringTrafficCoverage",
		trafficBody(websiteId),
		"Failed to load sitemap coverage"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, coverage: null };
	return { ok: true, message: parsed.message, coverage: parsed.data };
}

/** Search engine metrics — Google Search Console and Bing Webmaster (SearchData). */
export async function getMonitoringSearch(
	websiteId,
	{
		start,
		end,
		compareStart,
		compareEnd,
		searchType = "web",
		engine,
		connectionId,
		days,
	} = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, search: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringSearch",
		trafficBody(websiteId, {
			...(start ? { start } : {}),
			...(end ? { end } : {}),
			...(compareStart ? { compareStart } : {}),
			...(compareEnd ? { compareEnd } : {}),
			...(searchType ? { searchType } : {}),
			...(engine ? { engine } : {}),
			...(connectionId ? { connectionId } : {}),
			...(days != null ? { days: Number(days) } : {}),
		}),
		"Failed to load search performance"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, search: null };
	return { ok: true, message: parsed.message, search: parsed.data };
}

export async function syncMonitoringSearch(websiteId, { engine } = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringSearchSync",
		trafficBody(websiteId, {
			...(engine ? { engine } : {}),
		}),
		"Failed to sync search performance"
	);
}

/** Cross-source index state (TraceabilityData). */
export async function getMonitoringTraceability(
	websiteId,
	{ start, end, connectionId, assistants } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, traceability: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringTraceability",
		trafficBody(websiteId, {
			...(start ? { start } : {}),
			...(end ? { end } : {}),
			...(connectionId ? { connectionId } : {}),
			...(assistants ? { assistants } : {}),
		}),
		"Failed to load traceability"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, traceability: null };
	return { ok: true, message: parsed.message, traceability: parsed.data };
}

export async function inspectMonitoringSearch(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringSearchInspect",
		trafficBody(websiteId),
		"Failed to queue URL inspection"
	);
}

export async function listMonitoringConnectors() {
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringConnectors",
		{},
		"Failed to load connectors"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, connectors: [] };
	const connectors = Array.isArray(parsed.data?.connectors)
		? parsed.data.connectors
		: Array.isArray(parsed.data)
			? parsed.data
			: [];
	return { ok: true, message: parsed.message, connectors };
}

export async function listMonitoringConnections(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, connections: [] };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringConnections",
		trafficBody(websiteId),
		"Failed to load connections"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, connections: [] };
	const connections = Array.isArray(parsed.data?.connections)
		? parsed.data.connections
		: [];
	return { ok: true, message: parsed.message, connections };
}

export async function createMonitoringConnection(
	websiteId,
	{ connectorKey, transport, config, credentials } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, connection: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringCreateConnection",
		trafficBody(websiteId, {
			connectorKey,
			...(transport ? { transport } : {}),
			config: config || {},
			...(credentials ? { credentials } : {}),
		}),
		"Failed to create connection"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, connection: null };
	return { ok: true, message: parsed.message, connection: parsed.data };
}

export async function verifyMonitoringConnection(connectionId) {
	if (!connectionId) return { ok: false, message: "Missing connection id", connection: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringVerifyConnection",
		{ connectionId },
		"Failed to verify connection"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, connection: null };
	return { ok: true, message: parsed.message, connection: parsed.data };
}

export async function reconnectMonitoringConnection(connectionId) {
	if (!connectionId) return { ok: false, message: "Missing connection id", connection: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringReconnectConnection",
		{ connectionId },
		"Failed to reconnect"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, connection: null };
	return { ok: true, message: parsed.message, connection: parsed.data };
}

export async function deleteMonitoringConnection(connectionId) {
	if (!connectionId) return { ok: false, message: "Missing connection id" };
	return postMonitoring(
		"aiVisibilityMonitoringDeleteConnection",
		{ connectionId },
		"Failed to disconnect"
	);
}

/** Paginated history for the Analysis tab sidebar. */
export async function listMonitoringPromptAnalyses(
	websiteId,
	{ q, page = 1, pageSize = 8 } = {}
) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", page: null };
	}
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringPromptAnalyses",
		{
			websiteId: Number(websiteId),
			...(q ? { q } : {}),
			page: Number(page),
			pageSize: Number(pageSize),
		},
		"Could not load analysis history"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, page: null };
	return { ok: true, message: parsed.message, page: parsed.data };
}

export const WRITING_ASSISTANT_BUSY_STATUSES = new Set([
	"pending",
	"fetching_competitors",
	"competitors_ready",
	"fetching_own_page",
	"own_page_ready",
	"analyzing_gaps",
	"gaps_ready",
	"drafting",
]);

export async function getMonitoringWritingAssistantSetup(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, personas: [] };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantSetup",
		{ websiteId: Number(websiteId) },
		"Could not load Writing Assistant setup"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, personas: [] };
	return {
		ok: true,
		message: parsed.message,
		personas: Array.isArray(parsed.data?.personas) ? parsed.data.personas : [],
	};
}

export async function listMonitoringWritingAssistantRuns(
	websiteId,
	{ analysisId, limit } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, runs: [] };
	const body = { websiteId: Number(websiteId) };
	if (analysisId != null && analysisId !== "") body.analysisId = Number(analysisId);
	if (limit != null && limit !== "") body.limit = Number(limit);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantRuns",
		body,
		"Could not load Writing Assistant runs"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, runs: [] };
	const runs = Array.isArray(parsed.data)
		? parsed.data
		: Array.isArray(parsed.data?.runs)
			? parsed.data.runs
			: [];
	return { ok: true, message: parsed.message, runs };
}

export async function createMonitoringWritingAssistantRun(
	websiteId,
	{ analysisId, recommendationRef, mode, targetUrl, personaId } = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	const body = {
		websiteId: Number(websiteId),
		analysisId: Number(analysisId),
		recommendationRef,
		mode,
	};
	if (targetUrl != null && targetUrl !== "") body.targetUrl = targetUrl;
	if (personaId != null && personaId !== "") body.personaId = Number(personaId);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantCreateRun",
		body,
		"Could not start the Writing Assistant"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	return { ok: true, message: parsed.message, run: parsed.data };
}

export async function getMonitoringWritingAssistantRun(websiteId, runId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantRun",
		{ websiteId: Number(websiteId), runId: Number(runId) },
		"Could not load the Writing Assistant run"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	return { ok: true, message: parsed.message, run: parsed.data };
}

export async function draftMonitoringWritingAssistantRun(websiteId, runId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantDraftRun",
		{ websiteId: Number(websiteId), runId: Number(runId) },
		"Could not generate the Writing Assistant draft"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	return { ok: true, message: parsed.message, run: parsed.data };
}

export async function regenerateMonitoringWritingAssistantDraft(websiteId, runId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantRegenerateDraft",
		{ websiteId: Number(websiteId), runId: Number(runId) },
		"Could not regenerate draft"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	return { ok: true, message: parsed.message, run: parsed.data };
}

export async function saveMonitoringWritingAssistantContent(
	websiteId,
	runId,
	content = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, run: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantSaveContent",
		{ websiteId: Number(websiteId), runId: Number(runId), ...content },
		"Could not save the Writing Assistant draft"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, run: null };
	return { ok: true, message: parsed.message, run: parsed.data };
}

function unwrapPersonas(data) {
	if (Array.isArray(data?.personas)) return data.personas;
	if (Array.isArray(data)) return data;
	return [];
}

function unwrapPersona(data) {
	return data?.persona ?? data ?? null;
}

function unwrapBrandGuidelines(data) {
	if (data == null) return null;
	const value = data.brand_guidelines ?? data.brandGuidelines ?? data;
	if (value == null || typeof value !== "object") return null;
	return value;
}

function buildPersonaBody(websiteId, personaId, fields = {}) {
	const body = { websiteId: Number(websiteId) };
	if (personaId != null && personaId !== "") {
		body.personaId = Number(personaId);
	}
	const name = String(fields.name ?? "").trim();
	if (name) body.name = name;
	if (fields.gender != null && fields.gender !== "") body.gender = String(fields.gender);
	if (fields.ageRange != null && fields.ageRange !== "") {
		body.ageRange = String(fields.ageRange);
	}
	if (fields.location != null && fields.location !== "") {
		body.location = String(fields.location);
	}
	if (fields.profession != null && fields.profession !== "") {
		body.profession = String(fields.profession);
	}
	if (fields.tone != null && fields.tone !== "") body.tone = String(fields.tone);
	if (fields.notes != null && fields.notes !== "") body.notes = String(fields.notes);
	if (fields.language != null && fields.language !== "") {
		body.language = String(fields.language);
	}
	if (fields.likes != null && fields.likes !== "") body.likes = String(fields.likes);
	if (fields.dislikes != null && fields.dislikes !== "") {
		body.dislikes = String(fields.dislikes);
	}
	if (fields.interests != null && fields.interests !== "") {
		body.interests = String(fields.interests);
	}
	return body;
}

function buildBrandGuidelinesBody(websiteId, brandGuidelinesId, fields = {}) {
	const body = { websiteId: Number(websiteId) };
	if (brandGuidelinesId != null && brandGuidelinesId !== "") {
		body.brandGuidelinesId = Number(brandGuidelinesId);
	}
	if (fields.voice != null && fields.voice !== "") body.voice = String(fields.voice);
	if (Array.isArray(fields.doList)) body.doList = fields.doList;
	if (Array.isArray(fields.dontList)) body.dontList = fields.dontList;
	if (fields.examples != null && fields.examples !== "") {
		body.examples = String(fields.examples);
	}
	return body;
}

export async function listMonitoringWritingAssistantPersonas(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, personas: [] };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantPersonas",
		{ websiteId: Number(websiteId) },
		"Could not load personas"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, personas: [] };
	return {
		ok: true,
		message: parsed.message,
		personas: unwrapPersonas(parsed.data),
	};
}

export async function createMonitoringWritingAssistantPersona(websiteId, fields = {}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, persona: null };
	const body = buildPersonaBody(websiteId, undefined, fields);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantCreatePersona",
		body,
		"Could not create persona"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, persona: null };
	return {
		ok: true,
		message: parsed.message,
		persona: unwrapPersona(parsed.data),
	};
}

export async function updateMonitoringWritingAssistantPersona(
	websiteId,
	personaId,
	fields = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, persona: null };
	const body = buildPersonaBody(websiteId, personaId, fields);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantUpdatePersona",
		body,
		"Could not update persona"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, persona: null };
	return {
		ok: true,
		message: parsed.message,
		persona: unwrapPersona(parsed.data),
	};
}

export async function deleteMonitoringWritingAssistantPersona(websiteId, personaId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	return postMonitoring(
		"aiVisibilityMonitoringWritingAssistantDeletePersona",
		{ websiteId: Number(websiteId), personaId: Number(personaId) },
		"Could not delete persona"
	);
}

export async function getMonitoringWritingAssistantBrandGuidelines(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, guidelines: null };
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantBrandGuidelines",
		{ websiteId: Number(websiteId) },
		"Could not load brand guidelines"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, guidelines: null };
	const guidelines = unwrapBrandGuidelines(parsed.data);
	return {
		ok: true,
		message: parsed.message,
		guidelines: guidelines && Object.keys(guidelines).length > 0 ? guidelines : null,
	};
}

export async function createMonitoringWritingAssistantBrandGuidelines(
	websiteId,
	fields = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, guidelines: null };
	const body = buildBrandGuidelinesBody(websiteId, undefined, fields);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantCreateBrandGuidelines",
		body,
		"Could not create brand guidelines"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, guidelines: null };
	return {
		ok: true,
		message: parsed.message,
		guidelines: unwrapBrandGuidelines(parsed.data),
	};
}

export async function updateMonitoringWritingAssistantBrandGuidelines(
	websiteId,
	brandGuidelinesId,
	fields = {}
) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, guidelines: null };
	const body = buildBrandGuidelinesBody(websiteId, brandGuidelinesId, fields);
	const parsed = await postMonitoring(
		"aiVisibilityMonitoringWritingAssistantUpdateBrandGuidelines",
		body,
		"Could not update brand guidelines"
	);
	if (!parsed.ok) return { ok: false, message: parsed.message, guidelines: null };
	return {
		ok: true,
		message: parsed.message,
		guidelines: unwrapBrandGuidelines(parsed.data),
	};
}
